import type { AxiosResponse } from 'axios'
import { crmApi } from '@/api'
import type { ResponseWrapper } from '@/api/types/base.types'
import type { Lead, LeadState } from '@/api/types/crm/crm.types'
import type {
  CrmLeadCardInFunnelData,
  CrmLeadCardResponse,
} from '@/api/types/crm/crmFunnels.types'
import { isError } from '@/helpers/checkQuery'
import { getLeadVkChatMeta } from '../../utils/leadFields'
import { withLeadVkChatFromPayload } from '../../utils/leadVkChatMeta'
import type { LeadProfileLead } from './LeadProfileModal.types'

export const toValidFunnelId = (value: unknown): number | null => {
  const num = Number(value)
  return Number.isFinite(num) && num > 0 ? num : null
}

export type LeadCardApiData = CrmLeadCardResponse['data']
export type LeadCardCurrentFunnel = LeadCardApiData['current_funnels'][number]

const normalizeGetLeadPayload = (payload: unknown): Lead | undefined => {
  if (!payload || typeof payload !== 'object') {
    return undefined
  }

  if ('lead' in payload) {
    const nested = (payload as { lead?: unknown }).lead
    if (nested && typeof nested === 'object' && 'id' in nested) {
      return nested as Lead
    }
  }

  if ('id' in payload) {
    return payload as Lead
  }

  return undefined
}

/** `get-lead`: `integrations` / legacy `vk` на `data` или `data.lead`. */
const attachVkFromGetLeadBody = (lead: Lead, payload: unknown): Lead => {
  return withLeadVkChatFromPayload(lead, payload) as Lead
}

const getGetLeadResponseBodyPayload = (
  leadDetailsResponse: { data?: { data?: unknown; status?: string } } | undefined,
): unknown => {
  if (!leadDetailsResponse?.data) {
    return undefined
  }
  const body = leadDetailsResponse.data
  if (body.status === 'success' && body.data) {
    return body.data
  }
  return body.data
}

/** Разбор тела get-lead: `data.lead` или плоский лид; иногда нет `status: success`. */
export function tryParseLeadFromGetLeadResponse(
  leadDetailsResponse: { data?: { data?: unknown; status?: string } } | undefined,
): Lead | undefined {
  const payload = getGetLeadResponseBodyPayload(leadDetailsResponse)
  const parsed = normalizeGetLeadPayload(payload)
  if (!parsed) {
    return undefined
  }
  return attachVkFromGetLeadBody(parsed, payload)
}

export function parseFullLeadFromGetLeadResponse(
  leadDetailsResponse:
    | AxiosResponse<ResponseWrapper<unknown>, unknown>
    | undefined,
): Lead | undefined {
  const parsed = tryParseLeadFromGetLeadResponse(leadDetailsResponse)
  if (parsed !== undefined) return parsed
  if (
    leadDetailsResponse &&
    !isError(leadDetailsResponse as AxiosResponse<ResponseWrapper<any>, any>)
  ) {
    return normalizeGetLeadPayload(leadDetailsResponse?.data?.data)
  }
  return undefined
}

/**
 * Свести `user_id` после getLead + карточки.
 * Явный `user_id: null` от API и локальная отвязка (`user_id: null`, `user` очищен) имеют приоритет;
 * не подставлять устаревший `user.id` из того же ответа.
 * Если в `prev` нет `user_id`, но есть `user.id` (строка таблицы) — сохраняем id, пока API не даст свой.
 */
export function mergeLeadUserId(
  prev: LeadProfileLead,
  apiLead: Lead | undefined,
): number | null {
  const prevLead = prev as Lead
  const prevPositiveId = (): number | null => {
    const uid = prevLead.user_id
    if (uid === null) return null
    if (uid !== undefined && uid !== null) {
      const n = Number(uid)
      return Number.isFinite(n) && n > 0 ? n : null
    }
    const nested = prevLead.user?.id
    if (nested === undefined || nested === null) return null
    const n = Number(nested)
    return Number.isFinite(n) && n > 0 ? n : null
  }
  const prevId = prevPositiveId()

  if (apiLead) {
    if ('user_id' in apiLead && apiLead.user_id === null) {
      const nestedOnNull = apiLead.user?.id
      if (nestedOnNull !== undefined && nestedOnNull !== null) {
        const n = Number(nestedOnNull)
        if (Number.isFinite(n) && n > 0) return n
      }
      return null
    }
    const direct = apiLead.user_id
    if (direct !== undefined && direct !== null) {
      const n = Number(direct)
      if (Number.isFinite(n) && n > 0) return n
    }
    const nested = apiLead.user?.id
    if (nested !== undefined && nested !== null) {
      const n = Number(nested)
      if (Number.isFinite(n) && n > 0) {
        const locallyUnbound =
          prevLead.user_id === null &&
          (prevLead.user == null || prevLead.user === undefined)
        if (!locallyUnbound) return n
      }
    }
  }

  return prevId
}

function mergeLeadTypeForListContext(
  prev: LeadProfileLead,
  fullLeadFromDetails: Lead | undefined,
): LeadState | undefined {
  const apiLeadType = fullLeadFromDetails?.type
  let mergedLeadType: LeadState | undefined = prev.type as LeadState | undefined
  if (fullLeadFromDetails !== undefined && apiLeadType !== undefined) {
    if (
      (prev.type === 'processed' || prev.type === 'client') &&
      apiLeadType === 'in_work'
    ) {
      mergedLeadType = prev.type as LeadState
    } else {
      mergedLeadType = apiLeadType
    }
  }
  return mergedLeadType
}

/** Какую воронку считать текущей + строка карты для неё. */
export function resolveFunnelContextFromLeadCard(
  cardData: LeadCardApiData,
  requestedFunnelId: number | null,
): {
  currentFunnels: LeadCardCurrentFunnel[]
  resolvedFunnelId: number | null
  rowForResolved: LeadCardCurrentFunnel | undefined
} {
  const currentFunnels = Array.isArray(cardData.current_funnels)
    ? cardData.current_funnels
    : []

  const cardHasFunnelRow = (fid: number | null): boolean =>
    fid !== null &&
    currentFunnels.some((row) => Number(row.funnel_id) === fid)

  const fallbackFunnelId =
    toValidFunnelId(cardData.selected_funnel_id) ??
    toValidFunnelId(currentFunnels[0]?.funnel_id) ??
    null

  const requestedFunnelOk =
    requestedFunnelId !== null && cardHasFunnelRow(requestedFunnelId)

  const resolvedFunnelId = requestedFunnelOk
    ? requestedFunnelId
    : fallbackFunnelId

  const rowForResolved =
    resolvedFunnelId !== null
      ? currentFunnels.find(
          (item) => Number(item.funnel_id) === resolvedFunnelId,
        )
      : undefined

  return { currentFunnels, resolvedFunnelId, rowForResolved }
}

const inflightLeadCardInFunnel = new Map<
  string,
  Promise<CrmLeadCardInFunnelData | undefined>
>()

/**
 * Один in-flight GET `card-funnel` на пару lead/funnel[/lead_funnel] — модалка + статусы + селект
 * не дублируют один и тот же XHR (особенно заметно у МОП при смене воронки в UI).
 */
export function fetchLeadCardInFunnelDataDeduped(
  lead_id: number,
  funnel_id: number,
  lead_funnel_id?: number | null,
): Promise<CrmLeadCardInFunnelData | undefined> {
  const leadFunnelKey =
    lead_funnel_id != null &&
    Number.isFinite(Number(lead_funnel_id)) &&
    Number(lead_funnel_id) > 0
      ? String(Number(lead_funnel_id))
      : ''
  const key = `${lead_id}:${funnel_id}:${leadFunnelKey}`
  const hit = inflightLeadCardInFunnel.get(key)
  if (hit) return hit

  const p = crmApi.leads
    .getLeadCardInFunnel({
      lead_id,
      funnel_id,
      ...(leadFunnelKey
        ? { lead_funnel_id: Number(leadFunnelKey) }
        : {}),
    })
    .then((r) => r?.data?.data as CrmLeadCardInFunnelData | undefined)
    .finally(() => {
      if (inflightLeadCardInFunnel.get(key) === p) {
        inflightLeadCardInFunnel.delete(key)
      }
    })

  inflightLeadCardInFunnel.set(key, p)
  return p
}

/** Сбросить in-flight по паре lead/funnel — после мутаций state заявки, иначе можно получить старый ответ. */
export function invalidateLeadCardInFunnelDedup(
  lead_id: number,
  funnel_id: number,
  lead_funnel_id?: number | null,
) {
  const leadFunnelKey =
    lead_funnel_id != null &&
    Number.isFinite(Number(lead_funnel_id)) &&
    Number(lead_funnel_id) > 0
      ? String(Number(lead_funnel_id))
      : ''
  const key = `${lead_id}:${funnel_id}:${leadFunnelKey}`
  inflightLeadCardInFunnel.delete(key)
  // Старый ключ без lead_funnel_id — на случай параллельных вызовов.
  if (leadFunnelKey) {
    inflightLeadCardInFunnel.delete(`${lead_id}:${funnel_id}:`)
  }
}

/** Загрузка card-funnel и запись снимка; возвращает manager_id по связке. */
export async function fetchLeadCardInFunnelAndPutSnapshot(params: {
  numericLeadId: number
  resolvedFunnelId: number
  leadFunnelId?: number | null
  putCardInFunnelSnapshot: (funnelId: number, data: CrmLeadCardInFunnelData) => void,
}): Promise<number | null | undefined> {
  const {
    numericLeadId,
    resolvedFunnelId,
    leadFunnelId,
    putCardInFunnelSnapshot,
  } = params
  try {
    invalidateLeadCardInFunnelDedup(
      numericLeadId,
      resolvedFunnelId,
      leadFunnelId,
    )
    const cardInFunnelData = await fetchLeadCardInFunnelDataDeduped(
      numericLeadId,
      resolvedFunnelId,
      leadFunnelId,
    )
    if (cardInFunnelData) {
      const stampedLeadFunnelId =
        Number(cardInFunnelData.lead_funnel_id) > 0
          ? Number(cardInFunnelData.lead_funnel_id)
          : leadFunnelId != null &&
              Number.isFinite(Number(leadFunnelId)) &&
              Number(leadFunnelId) > 0
            ? Number(leadFunnelId)
            : undefined
      putCardInFunnelSnapshot(resolvedFunnelId, {
        ...cardInFunnelData,
        ...(stampedLeadFunnelId != null
          ? { lead_funnel_id: stampedLeadFunnelId }
          : {}),
      })
    }
    if (
      cardInFunnelData?.manager?.id !== undefined &&
      cardInFunnelData?.manager?.id !== null
    ) {
      return Number(cardInFunnelData.manager.id)
    }
    return null
  } catch {
    return undefined
  }
}

/** Только get-lead (например после чата ВК): поля лида и VK unread / integrations. */
export function mergeLeadProfileFromGetLeadOnly(
  prev: LeadProfileLead,
  fullLeadFromDetails: Lead | undefined,
): LeadProfileLead {
  if (!fullLeadFromDetails) {
    return prev
  }

  const apiVk = getLeadVkChatMeta(fullLeadFromDetails)
  const prevVk = getLeadVkChatMeta(prev)
  const nextVk = apiVk !== null ? apiVk : prevVk
  const apiIntegrations = (fullLeadFromDetails as { integrations?: unknown })
    .integrations
  const prevIntegrations = (prev as { integrations?: unknown }).integrations

  return {
    ...prev,
    ...fullLeadFromDetails,
    ...(nextVk ? { vk: nextVk } : {}),
    integrations:
      apiIntegrations !== undefined ? apiIntegrations : prevIntegrations,
  } as LeadProfileLead
}

/** Воронки лида для UI: unique по funnel_id (несколько сделок ≠ несколько воронок). */
function buildUniqueLeadFunnelsFromCurrentFunnels(params: {
  currentFunnels: LeadCardCurrentFunnel[]
  resolvedFunnelId: number | null
  managerIdFromCardFunnel: number | null | undefined
}): Array<{
  id: number
  lead_funnel_id: number
  name: string | null
  is_primary: boolean
  manager_id: number | null
  current_status_id: number | null
  entered_at: string | null
  next_contact_at: string | null
  task_counts?: { today: number; overdue: number } | null
}> {
  const { currentFunnels, resolvedFunnelId, managerIdFromCardFunnel } = params
  const byFunnelId = new Map<
    number,
    {
      id: number
      lead_funnel_id: number
      name: string | null
      is_primary: boolean
      manager_id: number | null
      current_status_id: number | null
      entered_at: string | null
      next_contact_at: string | null
      task_counts?: { today: number; overdue: number } | null
    }
  >()

  for (const item of currentFunnels) {
    const funnelId = Number(item.funnel_id)
    if (!Number.isFinite(funnelId) || funnelId <= 0) continue

    const dealId = Number(item.lead_funnel_id ?? item.id)
    const mapped = {
      id: funnelId,
      lead_funnel_id:
        Number.isFinite(dealId) && dealId > 0 ? dealId : Number(item.id),
      name: item.funnel_name,
      is_primary: !!item.is_primary,
      manager_id:
        resolvedFunnelId !== null &&
        funnelId === resolvedFunnelId &&
        managerIdFromCardFunnel !== undefined
          ? managerIdFromCardFunnel
          : item.manager_id ?? null,
      current_status_id: item.current_status_id ?? null,
      entered_at: item.entered_at ?? null,
      next_contact_at: item.next_contact_at ?? null,
      task_counts: item.task_counts ?? null,
    }

    const existing = byFunnelId.get(funnelId)
    if (!existing) {
      byFunnelId.set(funnelId, mapped)
      continue
    }

    // Одна воронка — одна опция; primary предпочтительнее произвольной сделки.
    if (mapped.is_primary && !existing.is_primary) {
      byFunnelId.set(funnelId, mapped)
    }
  }

  return Array.from(byFunnelId.values())
}

/** Склейка prev + get-lead + карта воронок + выбранная строка воронки. */
export function buildLeadAfterModalRefetch(params: {
  prev: LeadProfileLead
  fullLeadFromDetails: Lead | undefined
  cardData: LeadCardApiData
  resolvedFunnelId: number | null
  rowForResolved: LeadCardCurrentFunnel | undefined
  managerIdFromCardFunnel: number | null | undefined
  currentFunnels: LeadCardCurrentFunnel[]
}): LeadProfileLead {
  const {
    prev,
    fullLeadFromDetails,
    cardData,
    resolvedFunnelId,
    rowForResolved,
    managerIdFromCardFunnel,
    currentFunnels,
  } = params

  const merged: LeadProfileLead =
    fullLeadFromDetails !== undefined
      ? ({
          ...prev,
          ...fullLeadFromDetails,
        } as LeadProfileLead)
      : prev

  const mergedUserId = mergeLeadUserId(prev, fullLeadFromDetails)
  const mergedLeadType = mergeLeadTypeForListContext(prev, fullLeadFromDetails)

  const prevVk = getLeadVkChatMeta(prev)
  const apiVk = fullLeadFromDetails
    ? getLeadVkChatMeta(fullLeadFromDetails)
    : null
  const cardLeadVk = cardData.lead
    ? getLeadVkChatMeta(cardData.lead)
    : getLeadVkChatMeta(cardData)

  const nextVk =
    apiVk !== null
      ? apiVk
      : cardLeadVk !== null
        ? cardLeadVk
        : prevVk

  const apiIntegrations = fullLeadFromDetails
    ? (fullLeadFromDetails as { integrations?: unknown }).integrations
    : undefined
  const cardIntegrations = (cardData as { integrations?: unknown }).integrations
  const prevIntegrations = (prev as { integrations?: unknown }).integrations
  const nextIntegrations =
    apiIntegrations !== undefined
      ? apiIntegrations
      : cardIntegrations !== undefined
        ? cardIntegrations
        : prevIntegrations

  return {
    ...merged,
    ...(nextVk ? { vk: nextVk } : {}),
    ...(nextIntegrations !== undefined
      ? { integrations: nextIntegrations }
      : {}),
    user_id: mergedUserId,
    user:
      mergedUserId === null
        ? null
        : (merged as Lead).user ?? (prev as Lead).user ?? null,
    type: mergedLeadType,
    currentFunnelId: resolvedFunnelId,
    lead_funnel_id: rowForResolved
      ? Number(rowForResolved.id)
      : (merged as { lead_funnel_id?: number }).lead_funnel_id,
    currentFunnelStatusId:
      rowForResolved?.current_status_id ??
      (merged as { currentFunnelStatusId?: number | null })
        .currentFunnelStatusId ??
      null,
    currentFunnelStatusName:
      cardData.current_status?.name ??
      (merged as { currentFunnelStatusName?: string | null })
        .currentFunnelStatusName ??
      null,
    currentFunnelStatusColor:
      cardData.current_status?.color ??
      (merged as { currentFunnelStatusColor?: string | null })
        .currentFunnelStatusColor ??
      null,
    funnel: rowForResolved
      ? {
          id: Number(rowForResolved.funnel_id),
          name: rowForResolved.funnel_name,
        }
      : (merged as { funnel?: { id: number; name: string } | null }).funnel ?? null,
    funnels: buildUniqueLeadFunnelsFromCurrentFunnels({
      currentFunnels,
      resolvedFunnelId,
      managerIdFromCardFunnel,
    }),
    manager:
      cardData.manager !== undefined ? cardData.manager : merged.manager,
  } as LeadProfileLead
}
